import { fieldCannotBeBlankErrorMessage } from '@ben/columbia/components/shared/constants';
import Input from '@ben/orion/input';
import { isEmpty } from 'lodash';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import styled, { css } from 'styled-components';
import useMinMaxInput from '../../../../hooks/use-min-max-input';
import { hasActiveEligibilityRule } from '../../utils';
import FallbackRankSwitches from '../_common/fallback-rank-switches';
import MinMaxConfiguration from '../_common/min-max-configuration';
import ModularDropdownInputOptions from '../modular-dropdown-input/modular-dropdown-input-options';

export interface ModularMultiSelectInputProps {
  componentControllerName: string;
  hasSubform?: boolean;
}

const ModularMultiSelectInput = ({
  componentControllerName,
  hasSubform,
}: ModularMultiSelectInputProps) => {
  const form = useFormContext();
  const { control } = form;

  const { isFixedNumber, setIsFixedNumber, renderMinMaxInput } = useMinMaxInput(
    {
      componentControllerName,
      form,
      minFieldName: 'minSelections',
      maxFieldName: 'maxSelections',
      validationMessage: {
        min: 'Min selections should be less than or equal to max selections',
        max: 'Max selections should be greater than or equal to min selections',
      },
    }
  );

  const options = useWatch({
    control,
    name: `${componentControllerName}.options`,
  });

  const minSelections = useWatch({
    control,
    name: `${componentControllerName}.minSelections`,
    exact: true,
  });

  const maxSelections = useWatch({
    control,
    name: `${componentControllerName}.maxSelections`,
    exact: true,
  });

  const hasActiveRule = hasActiveEligibilityRule(options);

  const shouldRequireDefaultValue =
    Number(minSelections) === 1 && Number(maxSelections) === 1 && hasActiveRule;

  const labelName = shouldRequireDefaultValue
    ? 'Default value'
    : 'Default value (optional)';

  return (
    <>
      <ComponentsWrapper>
        <Controller
          name={`${componentControllerName}.defaultValue`}
          control={control}
          render={({ field, fieldState }) => (
            <Input
              {...field}
              name={labelName}
              hasError={!!fieldState.error}
              errorMessage={fieldState.error?.message}
            />
          )}
          rules={{
            validate: (value) => {
              if (shouldRequireDefaultValue && isEmpty(value)) {
                return fieldCannotBeBlankErrorMessage;
              }
              return true;
            },
          }}
        />
      </ComponentsWrapper>

      <MinMaxConfiguration
        isFixedNumber={isFixedNumber}
        setIsFixedNumber={setIsFixedNumber}
      />
      <ComponentsWrapper>
        {renderMinMaxInput({
          isMin: true,
          name: 'Min selections',
          hintText:
            'Defines the minimum number of options the employee is allowed to select.',
        })}
        {renderMinMaxInput({
          isMin: false,
          name: 'Max selections',
          hintText:
            'Defines the maximum number of options the employee is allowed to select.',
        })}
      </ComponentsWrapper>
      <FallbackRankSwitches componentControllerName={componentControllerName} />
      <Divider />
      <ModularDropdownInputOptions
        componentControllerName={componentControllerName}
        subTitleText="Add or edit the options to display in your multi-select component"
        isMultiSelectInput
        hasSubform={hasSubform}
      />
    </>
  );
};

export default ModularMultiSelectInput;

const ComponentsWrapper = styled.div`
  ${({ theme }) => css`
    width: 100%;
    display: flex;
    flex-direction: row;
    gap: ${theme.spacing(2)};
  `}
`;

const Divider = styled.div`
  ${({ theme }) => css`
    width: 100%;
    height: 1px;
    background-color: ${theme.palette.neutral[60]};
  `}
`;
